<template>
  <ol ref="root" class="sec-stepper">
    <li v-for="(step, i) in steps" :key="step.k" class="sec-step" data-reveal>
      <span class="o700 sec-step__num" aria-hidden="true">
        {{ i + 1 }}
        <span class="sec-step__lit">{{ i + 1 }}</span>
      </span>
      <span v-if="i < steps.length - 1" class="sec-step__line" aria-hidden="true">
        <span class="sec-step__fill"></span>
      </span>
      <div class="sec-step__body">
        <span class="m600 sec-label sec-step__k">{{ step.k }}</span>
        <h3 class="o600 sec-step__title">{{ step.title }}</h3>
        <p class="m500 sec-step__text">{{ step.text }}</p>
      </div>
    </li>
  </ol>
</template>

<script setup>
// Numbered process. The numbers light up 1 -> 2 -> 3 -> 4 as the visitor scrolls through
// the stepper: each step's filled circle pops in, then the connector fills toward the
// next step (horizontal on desktop, vertical under 900px). GSAP ScrollTrigger with scrub
// ties progress to the scroll position. Without motion every step renders lit.
// Desktop: equal columns across the full width, each step centred under its number, with
// the connector running centre to centre. Under 900px it is a left-aligned vertical list.
import { ref } from 'vue';
import { gsap } from '@/plugins/gsap';
import { useMotion } from '@/composables/security/useMotion';

defineProps({
  steps: { type: Array, required: true }, // [{ k, title, text }]
});

const root = ref(null);

useMotion(root, (el) => {
  const vertical = window.matchMedia('(max-width: 900px)').matches;
  const nums = gsap.utils.toArray('.sec-step__num', el);
  const lits = gsap.utils.toArray('.sec-step__lit', el);
  const fills = gsap.utils.toArray('.sec-step__fill', el);
  const axis = vertical ? 'scaleY' : 'scaleX';

  gsap.set(lits, { autoAlpha: 0, scale: 0.4 });
  gsap.set(fills, { [axis]: 0 });

  const tl = gsap.timeline({
    defaults: { ease: 'none' },
    scrollTrigger: {
      trigger: el,
      start: vertical ? 'top 75%' : 'top 80%',
      end: vertical ? 'bottom 60%' : 'top 35%',
      scrub: 0.6,
    },
  });

  lits.forEach((lit, i) => {
    tl.to(lit, { autoAlpha: 1, scale: 1, duration: 0.35, ease: 'back.out(2.2)' })
      .fromTo(nums[i], { scale: 1 }, { scale: 1.12, duration: 0.15, yoyo: true, repeat: 1 }, '<');
    if (fills[i]) tl.to(fills[i], { [axis]: 1, duration: 0.6 });
  });
});
</script>

<style scoped>
.sec-stepper {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  gap: var(--sec-s3);
}

.sec-step {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sec-s2);
  text-align: center;
}

/* Unlit circle: the outline left behind before a step lights up. */
.sec-step__num {
  position: relative;
  width: 52px;
  height: 52px;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  border-radius: 999px;
  border: 2px solid var(--sec-line-strong);
  background: var(--sec-surface);
  color: var(--sec-ink-3);
  font-size: 1.3rem;
  line-height: 1;
  z-index: 1;
}

/* Lit circle, layered on top; GSAP fades and scales it in. */
.sec-step__lit {
  position: absolute;
  inset: -2px;
  display: grid;
  place-items: center;
  border-radius: 999px;
  background: var(--sec-accent-strong);
  color: var(--sec-on-accent);
  box-shadow: 0 0 0 4px var(--sec-accent-ring);
}

/* From just right of this circle to just left of the next one (next column's centre,
   one grid gap away). */
.sec-step__line {
  position: absolute;
  top: 25px;
  left: calc(50% + 36px);
  right: calc(-50% - var(--sec-s3) + 36px);
  height: 2px;
  border-radius: 2px;
  background: var(--sec-line-strong);
  overflow: hidden;
}

.sec-step__fill {
  display: block;
  width: 100%;
  height: 100%;
  background: var(--sec-accent);
  transform-origin: left center;
}

.sec-step__body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  max-width: 300px;
  margin-top: var(--sec-s1);
}

.sec-step__title {
  font-size: 1.375rem;
  color: var(--sec-ink);
  margin: 0;
}

.sec-step__text {
  font-size: 1rem;
  line-height: 160%;
  color: var(--sec-ink-2);
}

@media only screen and (max-width: 900px) {
  .sec-stepper {
    grid-auto-flow: row;
    grid-template-columns: 1fr;
  }
  .sec-step {
    flex-direction: row;
    align-items: flex-start;
    text-align: left;
  }
  .sec-step__body {
    align-items: flex-start;
    max-width: none;
    margin-top: 0;
  }
  .sec-step__line {
    top: 60px;
    bottom: -16px;
    left: 25px;
    right: auto;
    width: 2px;
    height: auto;
  }
  .sec-step__fill {
    transform-origin: center top;
  }
}
</style>
